import type { ApiClient } from './createApiClient';
import { createApiClient } from './createApiClient';
import { createAuthDataTokenStorage, createLocalStorageTokenStorage } from './storage';

export interface CreateAppApiClientOptions {
  baseURL: string;
  /** Chave localStorage para limpar no 401 (ex.: 'e-learning-b2b-auth') */
  authKey: string;
  /** Chave do objeto de auth no localStorage (ex.: 'e-learning-b2b-auth-data'). Se informada, token/refreshToken são lidos e atualizados aí; assim o refresh encontra o refresh token e não redireciona para login. */
  authDataKey?: string;
  /** Getter do tenant-id (env, host, etc.) */
  getTenantId: () => string | null;
  /** Porta em modo standalone para redirect (ex.: '3001' ou '3000') */
  redirectPort: string;
  /** Base path quando não standalone (ex.: '/b2b' ou '/') */
  basePath: string;
  /** Retorna o idioma atual para o header Accept-Language (ex.: () => i18n.language || 'en-US') */
  getLanguage?: () => string;
}

/**
 * Factory do cliente API para apps (b2b/b2b2c).
 * Centraliza baseURL, tokenStorage, redirect no 401 e tenant-id.
 */
export function createAppApiClient(
  options: CreateAppApiClientOptions
): ApiClient {
  const { baseURL, authKey, authDataKey, getTenantId, redirectPort, basePath, getLanguage } = options;

  const tokenStorage = authDataKey
    ? createAuthDataTokenStorage(authDataKey)
    : createLocalStorageTokenStorage();

  function redirectToLogin(): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(authKey);
      if (authDataKey) localStorage.removeItem(authDataKey);
    } catch {
      // ignore
    }
    const isStandalone = window.location.port === redirectPort;
    const base = isStandalone ? '' : basePath;
    window.location.href = `${window.location.origin}${base}/`;
  }

  return createApiClient({
    baseURL,
    tokenStorage,
    tenantId: getTenantId,
    refreshTokenUrl: `${baseURL}v1/Auth/refresh`,
    onUnauthorized: redirectToLogin,
    getLanguage,
  });
}
